import Image from 'next/image';
import { useMemo } from 'react';

import { Badge, KeyValueRow, KeyValueTable, Text } from '@/design-system/components/ui';
import useGetChainInfo from '@/hooks/useGetChainInfo';

import { HibachiAffiliateReferredFeesLevel, Trigger } from '../../domain';

interface Props {
  trigger: Trigger;
}

export function HibachiAffiliatesTriggerFullDetails({ trigger }: Props) {
  const { getChainIconUrl } = useGetChainInfo();
  const chainId = trigger.context?.chainId || trigger.context?.chain_id || 8453;
  const chainName = chainId === 8453 ? 'Base' : `Chain ${chainId}`;
  const referredFeesLevel = trigger.context?.referredFeesLevel as HibachiAffiliateReferredFeesLevel | undefined;

  const chainIconUrl = useMemo(() => {
    return getChainIconUrl(chainId);
  }, [chainId, getChainIconUrl]);

  const chainDisplay = useMemo(() => {
    return (
      <div className="tw:flex tw:flex-row tw:gap-2 tw:items-center">
        {chainIconUrl && <Image src={chainIconUrl} width={16} height={16} alt={chainName} />}
        <span>{chainName}</span>
      </div>
    );
  }, [chainIconUrl, chainName]);

  const levelDisplay = useMemo(() => {
    if (!referredFeesLevel) return '-';
    const levelLabel = referredFeesLevel.charAt(0).toUpperCase() + referredFeesLevel.slice(1);
    return <Badge size="sm">{levelLabel}</Badge>;
  }, [referredFeesLevel]);

  const executedWhenDisplay = useMemo(() => {
    return (
      <Text as="span" variant="body-sm" color="muted" className="tw:inline tw:flex-wrap">
        Affiliate generates referral fees on{' '}
        <Badge size="sm" className="tw:align-middle">
          Hibachi
        </Badge>{' '}
        platform at {levelDisplay}
      </Text>
    );
  }, [levelDisplay]);

  return (
    <KeyValueTable>
      <KeyValueRow label="Name" value={trigger.name} />
      <KeyValueRow label="Description" value={trigger.description} />
      <KeyValueRow label="Chain" value={chainDisplay} />
      <KeyValueRow label="Affiliate Level" value={levelDisplay} />
      <KeyValueRow label="Executed when" value={executedWhenDisplay} />
      <KeyValueRow label="Argument conditions" value={trigger.conditionExpression ?? '-'} />
    </KeyValueTable>
  );
}
